import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authAtom, AuthState } from "../recoil/auth/atom";
import { useAuthLogin, useRefreshToken } from "../services/authService";
import { strings } from "../config/string";
import jwt_decode from "jwt-decode";
import Roles from "../constants/userRoles";

interface JwtPayload {
  exp: number;
  organizationId: string;
}

export const useAuth = () => {
  const setAuth = useSetRecoilState(authAtom);
  const auth = useRecoilValue(authAtom);
  const navigate = useNavigate();
  const { mutateAsync: loginUser } = useAuthLogin();
  const { mutateAsync: refresh } = useRefreshToken();

  const login = async (email: string, password: string): Promise<string> => {
    let loginError = "";
    await loginUser({ email, password }).then(
      (user) => {
        if (user.isAuthenticated) {
          if (user.roles.some((role: string) => role === Roles.Admin || role === Roles.User)) {
            onSuccess(user);
          } else {
            loginError = strings.loginRoleError;
          }
        } else {
          loginError = strings.loginError;
        }
      },
      () => {
        loginError = strings.loginError;
      }
    );

    return loginError;
  };

  const onSuccess = (user: AuthState): void => {
    setAuth(user);
    localStorage.setItem("user", JSON.stringify(user));
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!auth?.id) return false;
    
    const result = await refresh(auth.id)
      .then(
        (user) => {
          onSuccess(user);
          return true;
        },
        () => {
          logout();
          return false;
        }
      );
    
    return result;
  };

  const logout = (): void => {
    localStorage.removeItem("user");
    setAuth(null);
    navigate("/login");
  };

  const isAuthenticated = (): boolean => {
    // Changed to make typecheck happy
    const tokenCheck = async (): Promise<boolean> => {
      return await checkToken();
    };
    
    return !!auth && !!auth.token && tokenCheck() as unknown as boolean;
  };

  const checkToken = async (): Promise<boolean> => {
    if (!auth?.token) return false;
    
    const decoded = jwt_decode<JwtPayload>(auth.token);

    if (auth.refreshTokenExpiration) {
      if (decoded.exp * 1000 < new Date().getTime()) {
        return await refreshToken();
      }
    }
    
    return true;
  };

  return {
    login,
    logout,
    isAuthenticated,
    refreshToken,
    orgId: auth?.token ? jwt_decode<JwtPayload>(auth.token).organizationId : null,
  };
};